/**
 * Pure helpers for Settings ▸ Model policy ▸ Copy / Paste: moving a policy
 * from one server to another as text. The server checks every rule again;
 * this only catches text that is not a copied policy before asking it.
 */
import type { CopiedPolicy, PastePreview, PastedRuleStatus } from '../types';

export const POLICY_FORMAT = 'model-policy/1';

/** The pasted text as a copied policy, or the reason it is not one. */
export function readPolicyText(text: string): { policy: CopiedPolicy } | { error: string } {
  if (!text.trim()) return { error: 'Paste a copied policy first.' };
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { error: 'That is not a copied policy (it is not JSON).' };
  }
  const obj = data as Partial<CopiedPolicy> | null;
  if (!obj || typeof obj !== 'object' || obj.format !== POLICY_FORMAT) {
    return { error: `That is not a copied policy (expected format ${POLICY_FORMAT}).` };
  }
  if (!Array.isArray(obj.rules)) return { error: 'The copied policy has no rules.' };
  return { policy: obj as CopiedPolicy };
}

const WORDS: Record<PastedRuleStatus, string> = {
  same: 'same models here',
  differs: 'different models here',
  missing: 'no model here',
  invalid: 'cannot be used here (skipped)',
};

const ORDER: PastedRuleStatus[] = ['same', 'differs', 'missing', 'invalid'];

/** "12 rules: 10 same models here · 1 no model here". */
export function pasteSummary(counts: PastePreview['counts']): string {
  const total = ORDER.reduce((n, s) => n + (counts[s] ?? 0), 0);
  if (!total) return 'The copied policy has no rules.';
  const parts = ORDER.filter((s) => counts[s]).map((s) => `${counts[s]} ${WORDS[s]}`);
  return `${total} rule${total === 1 ? '' : 's'}: ${parts.join(' · ')}`;
}

/** Whether every rule would run exactly the models it ran where it was copied. */
export function runsTheSame(counts: PastePreview['counts']): boolean {
  return Boolean(counts.same) && !counts.differs && !counts.missing && !counts.invalid;
}

/** Whether pasting would make a draft with any rules at all. */
export function pasteWouldWrite(counts: PastePreview['counts']): boolean {
  return Boolean(counts.same || counts.differs || counts.missing);
}
